import { Stack } from '@mui/material';
import { GridRowParams, GridValidRowModel, useGridApiRef } from '@mui/x-data-grid-pro';
import { Widget } from '@procureai/ui-mui';
import { isNil, prop } from 'ramda';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { BaseDetailDomainType, I18nNamespace } from '~/common/types';
import { SourcingProjectRequestItemField } from '~/common/types/cubes/SourcingProjectRequestProposalItem';
import {
  ColumnSelector,
  DataGridProStyled,
  DataGridWidgetHeader,
  ResetRemoteStateButton,
  useDataGridRemoteState,
} from '~/components/DataGridCustom';
import { RowDetailPanel } from '~/components/DataGridCustom/components/RowDetailPanel/RowDetailPanel';

import { SourcingProjectRequestListWidgetHeader } from './components/SourcingProjectLotItemListWidgetHeader/SourcingProjectRequestListWidgetHeader';
import { useSourcingProjectRequestGridConfig } from './SourcingProjectRequestListWidget.dataGrid';
import { useSourcingProjectRequestList } from './SourcingProjectRequestListWidget.hooks';

const domain = BaseDetailDomainType.CONTRACT_DETAILS;
export const SourcingProjectRequestListWidget = () => {
  const { t } = useTranslation(I18nNamespace.base, {
    keyPrefix: 'SourcingProjectLotItemListWidget',
  });
  const { loading, items } = useSourcingProjectRequestList();

  const getDetailPanelContent = useCallback(<R extends GridValidRowModel = any>({ row }: GridRowParams<R>) => {
    return <RowDetailPanel description={row.lotItemDescription} />;
  }, []);
  const apiRef = useGridApiRef();
  const config = useSourcingProjectRequestGridConfig();

  const {
    loading: loadingRemoteState,
    gridProps,
    onRemoteStateReset,
  } = useDataGridRemoteState({
    apiRef,
    config,
    domain,
  });

  const skip = loadingRemoteState || isNil(items);

  return (
    <Widget>
      <SourcingProjectRequestListWidgetHeader title={t('title')} />
      <DataGridWidgetHeader
        disablePadding
        right={
          <Stack direction="row">
            <ColumnSelector
              columns={gridProps.columns}
              columnVisibilityModel={gridProps.columnVisibilityModel}
              onColumnVisibilityModelChange={gridProps.onColumnVisibilityModelChange}
            />
            <ResetRemoteStateButton onRemoteStateReset={onRemoteStateReset} />
          </Stack>
        }
      />
      {!skip ? (
        <DataGridProStyled
          {...config}
          apiRef={apiRef}
          hideFooter
          disableColumnMenu
          loading={loading}
          rows={items}
          rowSelection={false}
          getRowId={prop(SourcingProjectRequestItemField.LOT_ITEM_ID)}
          getDetailPanelContent={getDetailPanelContent}
          {...gridProps}
        />
      ) : null}
    </Widget>
  );
};
